import React from 'react';
import { Autocomplete, AutocompleteRenderInputParams, TextField } from '@mui/material';
import { resolveObjectFromString } from '../../../../util/validation';
import {
  ViewExtended,
  getViews,
  view_label,
  resolveViewName
} from '../../../../lib/app/navigation';

interface ViewSelectProps {
  handleChangeViews?: (views: Array<ViewExtended> | ViewExtended | null) => void;
  options?: Array<ViewExtended>;
  selected_options?: Array<ViewExtended> | ViewExtended | null;
  disabled?: boolean;
  input_label?: string;
  group_by?: 'module.name';
  multi_select?: boolean;
  tag_limit?: number;
  clearable?: boolean;
  searchable?: boolean;
  module_id?: string;
  loading?: boolean;
  color?: string;
  disable_underline?: boolean;
  companyId?: string;
}

/**
 * View selection auto-complete component that handles a variety of built-in scenarios.
 * @param {Array<ViewExtended> | ViewExtended | null} props.handleChangeViews Callback function to notify the parent component when the selected options have changed.
 * @param {Array<ViewExtended>} props.options Custom list of options provided by the parent component. Defaults to none.
 * @param {Array<ViewExtended> | ViewExtended | null} props.selected_options Custom list of selected options provided by the parent component. Changes component from an uncontrolled to controlled.
 * @param {boolean} props.disabled Renders the component in a disabled state along with any other provided components. Defaults to enabled.
 * @param {string} props.input_label Sets a custom component input label. Defaults to the singular version of the main entity name.
 * @param {string} props.group_by Group the options by any one of a variety of grouping options. Defaults to ungrouped.
 * @param {boolean} props.multi_select Allow multi option selection and keeps the select open while selecting. Defaults to a single select and closes after selecting an option.
 * @param {number} props.tag_limit Sets a limit on the amount of tags rendered in the input. Defaults to unlimited.
 * @param {boolean} props.clearable Allows the autocomplete to be cleared. Defaults to true.
 * @param {boolean} props.searchable Allows the user to type to search in the autocomplete. Defaults to true.
 * @param {string} props.module_id Query views for the provided module.
 * @param {boolean} props.loading A flag to set the autocomplete into a loading state for things such as fetching entries from database.
 * @param {string} props.color A custom color used for the input component of the autocomplete. Defaults to standard MUI color.
 * @param {string} props.disable_underline Allows the hiding of the underline on a standard variant text field.
 * @returns {JSX.Element}
 */
const ViewSelect = (props: ViewSelectProps) => {
  const [optionList, setOptions] = React.useState<Array<ViewExtended>>([]);
  const [selectedOptions, setSelectedOptions] = React.useState<
    Array<ViewExtended> | ViewExtended | null
  >(props.multi_select ? [] : null);

  // Define autocomplete options
  React.useEffect(() => {
    (async () => {
      const options: Array<ViewExtended> = props.options
        ? // Views supplied by the parent component.
          props.options
        : // Views for specific or all Modules
          getViews(props.module_id);
      setOptions(options);
    })();
  }, [props.module_id, props.options]);

  // Define selected options
  React.useEffect(() => {
    setSelectedOptions(
      props.multi_select
        ? Array.isArray(props.selected_options)
          ? props.selected_options
          : []
        : props.selected_options
          ? props.selected_options
          : null
    );
  }, [props.options, props.selected_options]);

  return (
    <Autocomplete
      id="view-select"
      fullWidth
      disabled={props.disabled}
      loading={props.loading}
      limitTags={props.tag_limit}
      disableClearable={props.clearable === false}
      options={optionList
        .sort((a: ViewExtended, b: ViewExtended) => (a.name < b.name ? 1 : -1))
        .sort((a: ViewExtended, b: ViewExtended) => (a.module.id > b.module.id ? 1 : -1))}
      groupBy={
        props.group_by
          ? (option: ViewExtended) =>
              props.group_by ? resolveObjectFromString(props.group_by, option) : ''
          : undefined
      }
      value={selectedOptions}
      getOptionLabel={(option: ViewExtended) => {
        return resolveViewName(option, props.companyId) ?? option.name;
      }}
      disableCloseOnSelect={props.multi_select}
      isOptionEqualToValue={(option: ViewExtended, value: ViewExtended) =>
        option.id === value.id && option.module.id === value.module.id
      }
      multiple={props.multi_select}
      noOptionsText={`No ${view_label.many.toLowerCase()}`}
      renderInput={(params: AutocompleteRenderInputParams) => (
        <TextField
          {...params}
          label={props.input_label ?? view_label.one}
          variant="standard"
          inputProps={{ ...params.inputProps, readOnly: props.searchable === false }}
          InputProps={{ ...params.InputProps, disableUnderline: props.disable_underline }}
          sx={{
            input: { color: props.color ? props.color : 'currentcolor' },
            '& .MuiSvgIcon-root': {
              color: props.color ? props.color : 'currentcolor'
            }
          }}
        />
      )}
      onChange={(
        event: React.SyntheticEvent<Element, Event>,
        value: Array<ViewExtended> | ViewExtended | null
      ) => {
        setSelectedOptions(value);
        props.handleChangeViews && props.handleChangeViews(value);
      }}
    />
  );
};

export default ViewSelect;
